/* eslint-disable max-lines-per-function */
import { X } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { Button } from "../../../../shared/components/ui/button";
import { Input } from "../../../../shared/components/ui/input";
import { Label } from "../../../../shared/components/ui/label";
import type { CrewMember } from "../types";

interface EditCrewModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly titleName: string;
  readonly crewData: CrewMember[] | null;
  readonly onSave: (crewData: CrewMember[]) => Promise<void>;
}

/**
 * Modal for editing crew data
 * Allows adding/removing crew members with their roles
 */
export function EditCrewModal({ open, onClose, crewData, onSave }: Readonly<EditCrewModalProps>): ReactElement | null {
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [saving, setSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Crew input state
  const [crewName, setCrewName] = useState("");
  const [crewJob, setCrewJob] = useState("");

  // Initialize from props when modal opens
  useEffect(() => {
    if (open) {
      setCrew(crewData || []);
      setIsClosing(false);
    }
  }, [open, crewData]);

  if (!open && !isClosing) return null;

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const handleAddCrew = () => {
    if (!crewName.trim() || !crewJob.trim()) return;
    setCrew([...crew, { name: crewName.trim(), job: crewJob.trim() }]);
    setCrewName("");
    setCrewJob("");
  };

  const handleRemoveCrew = (index: number) => {
    setCrew(crew.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(crew);
      handleClose();
    } catch (error) {
      console.error("Failed to save crew:", error);
    } finally {
      setSaving(false);
    }
  };

  const isVisible = open && !isClosing;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
    >
      {/* Backdrop overlay */}
      <button
        type="button"
        className={`absolute inset-0 bg-black/60 cursor-default ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
        onClick={handleClose}
        aria-label="Close modal"
      />

      {/* Modal content */}
      <div
        className={`relative bg-neutral-900/40 backdrop-blur-md border border-neutral-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${isVisible ? "animate-in zoom-in-95 fade-in duration-200" : "animate-out zoom-out-95 fade-out duration-200"}`}
      >
        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Crew Members</h3>

            {/* Add Crew Form */}
            <div className="grid grid-cols-[1fr,1fr,auto] gap-2 mb-4">
              <div>
                <Label className="text-xs text-neutral-400">Name</Label>
                <Input
                  type="text"
                  placeholder="Jane Smith"
                  value={crewName}
                  onChange={(e) => setCrewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCrew();
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Role</Label>
                <Input
                  type="text"
                  placeholder="Director of Photography"
                  value={crewJob}
                  onChange={(e) => setCrewJob(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCrew();
                    }
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  onClick={handleAddCrew}
                  disabled={!crewName.trim() || !crewJob.trim()}
                  className="bg-lumi-accent hover:bg-lumi-accent/50 text-black"
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Crew List */}
            <div className="space-y-2">
              {crew.length === 0 ? (
                <p className="text-sm text-neutral-600 italic py-4">No crew members added yet</p>
              ) : (
                crew.map((member, index) => (
                  <div
                    key={`${member.name}-${member.job}-${index}`}
                    className="flex items-center justify-between bg-black/20 border border-neutral-800 px-4 py-3 rounded-lg hover:border-neutral-700 transition-colors"
                  >
                    <div className="flex-1">
                      <span className="text-sm text-white font-medium">{member.name}</span>
                      <span className="text-sm text-neutral-400 ml-3">• {member.job}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveCrew(index)}
                      className="text-neutral-500 hover:text-red-400 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-800">
          <Button type="button" onClick={handleClose} variant="ghost-sm" disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving} variant="ghost-sm">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
